/**
 * Company Settings API Service
 *
 * Manages company branding and profile data stored in Supabase.
 * Single-row table — one company per app instance.
 */

import { supabase } from './client';

// ============================================
// Types
// ============================================

export interface CompanySettingsRow {
  id: string;
  company_name: string;
  full_company_name: string | null;
  tagline: string | null;
  logo_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
  primary_color: string | null;
  website: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export interface CompanySettings {
  id: string;
  companyName: string;
  fullCompanyName: string;
  tagline: string;
  logoUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  website: string;
  phone: string;
  email: string;
  address: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompanySettingsUpdate {
  companyName?: string;
  fullCompanyName?: string;
  tagline?: string;
  logoUrl?: string | null;
  logoDarkUrl?: string | null;
  faviconUrl?: string | null;
  primaryColor?: string;
  website?: string;
  phone?: string;
  email?: string;
  address?: string;
}

// ============================================
// Mappers
// ============================================

const mapRowToData = (row: CompanySettingsRow): CompanySettings => ({
  id: row.id,
  companyName: row.company_name,
  fullCompanyName: row.full_company_name || '',
  tagline: row.tagline || '',
  logoUrl: row.logo_url,
  logoDarkUrl: row.logo_dark_url,
  faviconUrl: row.favicon_url,
  primaryColor: row.primary_color || '#2970FF',
  website: row.website || '',
  phone: row.phone || '',
  email: row.email || '',
  address: row.address || '',
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapUpdateToRow = (data: CompanySettingsUpdate): Record<string, unknown> => {
  const row: Record<string, unknown> = {};
  if (data.companyName !== undefined) row.company_name = data.companyName;
  if (data.fullCompanyName !== undefined) row.full_company_name = data.fullCompanyName;
  if (data.tagline !== undefined) row.tagline = data.tagline;
  if (data.logoUrl !== undefined) row.logo_url = data.logoUrl;
  if (data.logoDarkUrl !== undefined) row.logo_dark_url = data.logoDarkUrl;
  if (data.faviconUrl !== undefined) row.favicon_url = data.faviconUrl;
  if (data.primaryColor !== undefined) row.primary_color = data.primaryColor;
  if (data.website !== undefined) row.website = data.website;
  if (data.phone !== undefined) row.phone = data.phone;
  if (data.email !== undefined) row.email = data.email;
  if (data.address !== undefined) row.address = data.address;
  return row;
};

// ============================================
// Default Settings
// ============================================

const DEFAULT_SETTINGS: CompanySettings = {
  id: '',
  companyName: 'Relight',
  fullCompanyName: 'Relight Exterior Lighting',
  tagline: 'Residential, Commercial & HOA',
  logoUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  primaryColor: '#2970FF',
  website: '',
  phone: '',
  email: '',
  address: '',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// ============================================
// API Class
// ============================================

class CompanySettingsApi {
  /**
   * Get company settings (returns the single row, or defaults)
   */
  async get(): Promise<CompanySettings> {
    try {
      const { data, error } = await supabase
        .from('company_settings')
        .select('*')
        .limit(1)
        .single();

      if (error) throw error;
      return mapRowToData(data as CompanySettingsRow);
    } catch (err: any) {
      // Table might not exist yet or no rows — return defaults
      console.warn('Failed to load company settings:', err.message);
      return { ...DEFAULT_SETTINGS };
    }
  }

  /**
   * Update company settings (upserts the single row)
   */
  async update(updates: CompanySettingsUpdate): Promise<CompanySettings> {
    const rowData = mapUpdateToRow(updates);

    // Try to get existing row first
    const { data: existing } = await supabase
      .from('company_settings')
      .select('id')
      .limit(1)
      .single();

    if (existing?.id) {
      // Update existing row
      const { data, error } = await supabase
        .from('company_settings')
        .update(rowData)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return mapRowToData(data as CompanySettingsRow);
    } else {
      // Insert new row
      const { data, error } = await supabase
        .from('company_settings')
        .insert({ company_name: 'Relight', ...rowData })
        .select()
        .single();

      if (error) throw error;
      return mapRowToData(data as CompanySettingsRow);
    }
  }

  /**
   * Upload a company logo to Supabase storage
   * Returns the public URL
   */
  async uploadLogo(file: File, type: 'logo' | 'logo_dark' | 'favicon' = 'logo'): Promise<string> {
    const ext = file.name.split('.').pop() || 'png';
    const fileName = `${type}-${Date.now()}.${ext}`;
    const filePath = `company/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('company-assets')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('company-assets')
      .getPublicUrl(filePath);

    return data.publicUrl;
  }

  /**
   * Delete old logo from storage
   */
  async deleteLogo(url: string): Promise<void> {
    try {
      // Extract path from URL
      const match = url.match(/company-assets\/(.+)$/);
      if (match) {
        await supabase.storage.from('company-assets').remove([match[1]]);
      }
    } catch (err) {
      // Non-critical — old file stays in storage
      console.warn('Failed to delete old logo:', err);
    }
  }
}

// ============================================
// Singleton Export
// ============================================

export const companySettingsApi = new CompanySettingsApi();

// ============================================
// Synchronous Store (for non-React code: PDFs, emails, AI agent)
// ============================================

let _companyStore: CompanySettings = { ...DEFAULT_SETTINGS };

/** Called by CompanySettingsProvider to keep the store in sync */
export function setCompanyStore(settings: CompanySettings): void {
  _companyStore = { ...settings };
}

/** Get company settings synchronously (non-React). Falls back to defaults. */
export function getCompanyInfo(): CompanySettings {
  return _companyStore;
}
